import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ShoppingBag, Quote } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { Stars } from "./Stars";
import { inr } from "../lib/format";
import { toast } from "sonner";

// Curated community try-ons that rotate weekly.
const COMMUNITY = [
  { id: "banarasi-ruby", who: "Ananya R.", quote: "Wore it to my sister's wedding and felt like royalty.", rating: 5 },
  { id: "kanjeevaram-rose-gold", who: "Lakshmi V.", quote: "That temple border in real gold — simply unmatched.", rating: 5 },
  { id: "chikankari-mint", who: "Sara M.", quote: "So dreamy and light. My favourite daytime drape.", rating: 5 },
  { id: "tashore-magenta", who: "Priya N.", quote: "Rustic and rich all at once. Endless compliments!", rating: 4 },
  { id: "banarasi-bridal-crimson", who: "Meera K.", quote: "My bridal moment, and Tashora nailed it.", rating: 5 },
];

export const LookOfTheWeek = () => {
  const [products, setProducts] = useState({});
  const [i, setI] = useState(0);
  const paused = useRef(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products").then((r) => {
      const map = {};
      r.data.forEach((p) => { map[p.id] = p; });
      setProducts(map);
    }).catch(() => {});
  }, []);

  const looks = useMemo(() => COMMUNITY.filter((c) => products[c.id]).map((c) => ({ ...c, product: products[c.id] })), [products]);

  useEffect(() => {
    if (looks.length === 0) return;
    const t = setInterval(() => { if (!paused.current) setI((v) => (v + 1) % looks.length); }, 5000);
    return () => clearInterval(t);
  }, [looks.length]);

  if (looks.length === 0) return null;
  const look = looks[i % looks.length];
  const p = look.product;

  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-20" data-testid="look-of-the-week"
      onMouseEnter={() => { paused.current = true; }} onMouseLeave={() => { paused.current = false; }}>
      <div className="mb-10 flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-gold" />
        <div>
          <span className="caption">Community spotlight</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-gold-light mt-1">Look of the Week</h2>
        </div>
      </div>

      <div className="relative rounded-2xl overflow-hidden bg-obsidian-2 gold-border grain">
        <div className="grid md:grid-cols-2 items-stretch">
          <div className="relative min-h-[360px] md:min-h-[520px]">
            <AnimatePresence mode="wait">
              <motion.img
                key={look.id}
                src={p.image}
                alt={p.name}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="lotw-image"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-obsidian-2 via-transparent to-transparent" />
          </div>

          <div className="p-8 sm:p-12 flex flex-col justify-center">
            <AnimatePresence mode="wait">
              <motion.div key={look.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                <Quote className="w-8 h-8 text-gold/40" />
                <p className="font-serif text-2xl sm:text-3xl text-gold-light leading-snug mt-3" data-testid="lotw-quote">“{look.quote}”</p>
                <div className="flex items-center gap-3 mt-5">
                  <span className="w-9 h-9 rounded-full bg-gold/20 grid place-items-center text-gold font-semibold">{look.who[0]}</span>
                  <div>
                    <p className="text-sm text-gold-muted">{look.who}</p>
                    <Stars rating={look.rating} size={12} />
                  </div>
                </div>
                <div className="hairline my-6" />
                <p className="caption">Wearing</p>
                <p className="font-serif text-xl text-gold-light mt-1">{p.name} · <span className="font-mono text-gold text-base">{inr(p.price)}</span></p>
                <div className="flex flex-wrap gap-3 mt-6">
                  <button
                    onClick={() => { addItem(p); toast.success(`${p.name} added to cart`); }}
                    data-testid="lotw-shop-button"
                    className="inline-flex items-center gap-2 bg-gold text-obsidian font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors"
                  >
                    <ShoppingBag className="w-4 h-4" /> Shop this saree
                  </button>
                  <button
                    onClick={() => navigate(`/try-on?product=${p.id}`)}
                    data-testid="lotw-tryon-button"
                    className="inline-flex items-center gap-2 border border-gold/40 text-gold-muted px-5 py-2.5 rounded-full hover:bg-gold/10 transition-colors"
                  >
                    <Sparkles className="w-4 h-4 text-gold" /> Try this on me
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex gap-2 mt-8">
              {looks.map((lk, idx) => (
                <button
                  key={lk.id}
                  onClick={() => setI(idx)}
                  data-testid={`lotw-dot-${idx}`}
                  aria-label={`Look ${idx + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${idx === (i % looks.length) ? "w-8 bg-gold" : "w-2.5 bg-gold/30"}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
